import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { supabase } from '@/api/supabaseClient';

const COLORS = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', 
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'
];

export default function InstallerManagement({ onClose }) {
  const [installers, setInstallers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [newInstallerName, setNewInstallerName] = useState('');
  const [newInstallerColor, setNewInstallerColor] = useState(COLORS[0]);
  const [availability, setAvailability] = useState({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: false,
    sunday: false
  });

  useEffect(() => {
    loadInstallers();
  }, []);

  const loadInstallers = async () => {
    const { data, error } = await supabase
      .from('installers')
      .select('*')
      .order('name');
    
    if (error) {
      console.error('Error loading installers:', error);
      return;
    }
    
    setInstallers(data || []);
  };

  const addInstaller = async () => {
    console.log('Add installer clicked, name:', newInstallerName);
    
    if (!supabase) {
      alert('Supabase is not configured. Please set up your Supabase connection first.');
      return;
    }
    
    if (!newInstallerName.trim()) {
      alert('Please enter an installer name');
      return;
    }

    console.log('Attempting to add installer to Supabase...');
    const { error } = await supabase
      .from('installers')
      .insert([{
        name: newInstallerName.trim(),
        color: newInstallerColor,
        recurring_availability: availability
      }]);

    if (error) {
      console.error('Error adding installer:', error);
      if (error.code === '42P01') {
        alert('Database tables not set up yet. Please run the SQL migration from supabase_scheduling_schema.sql in your Supabase SQL Editor.');
      } else {
        alert(`Failed to add installer: ${error.message}`);
      }
      return;
    }

    setNewInstallerName('');
    setNewInstallerColor(COLORS[0]);
    setAvailability({
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    });
    loadInstallers();
  };

  const deleteInstaller = async (id) => {
    if (!confirm('Are you sure you want to delete this installer? This will not delete their scheduled installs.')) {
      return;
    }

    const { error } = await supabase
      .from('installers')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting installer:', error);
      alert('Failed to delete installer');
      return;
    }

    loadInstallers();
  };

  const updateAvailability = async (installer, newAvailability) => {
    const { error } = await supabase
      .from('installers')
      .update({ recurring_availability: newAvailability })
      .eq('id', installer.id);

    if (error) {
      console.error('Error updating availability:', error);
      alert('Failed to update availability');
      return;
    }

    loadInstallers();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Installation Team</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Installer */}
          <Card className="p-4">
            <h3 className="font-semibold mb-4">Add New Installer</h3>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={newInstallerName}
                  onChange={(e) => setNewInstallerName(e.target.value)}
                  placeholder="Installer name"
                />
              </div>

              <div>
                <Label>Color</Label>
                <div className="flex gap-2 mt-2">
                  {COLORS.map(color => (
                    <button
                      key={color}
                      className={`w-8 h-8 rounded border-2 ${
                        newInstallerColor === color ? 'border-slate-900' : 'border-slate-200'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewInstallerColor(color)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <Label>Recurring Availability</Label>
                <div className="grid grid-cols-7 gap-2 mt-2">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={`new-${day}`}
                        checked={availability[day]}
                        onCheckedChange={(checked) => 
                          setAvailability({ ...availability, [day]: checked })
                        }
                      />
                      <label
                        htmlFor={`new-${day}`}
                        className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {day.slice(0, 3)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={addInstaller} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Installer
              </Button>
            </div>
          </Card>

          {/* Existing Installers */}
          <div className="space-y-3">
            <h3 className="font-semibold">Current Team</h3>
            {installers.map(installer => (
              <Card key={installer.id} className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-6 h-6 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: installer.color }}
                    />
                    <div>
                      <div className="font-semibold">{installer.name}</div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteInstaller(installer.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div>
                  <Label className="text-xs">Recurring Availability</Label>
                  <div className="grid grid-cols-7 gap-2 mt-2">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                      <div key={day} className="flex items-center space-x-1">
                        <Checkbox
                          id={`${installer.id}-${day}`}
                          checked={installer.recurring_availability?.[day] ?? true}
                          onCheckedChange={(checked) => {
                            const newAvailability = {
                              ...(installer.recurring_availability || {}),
                              [day]: checked
                            };
                            updateAvailability(installer, newAvailability);
                          }}
                        />
                        <label
                          htmlFor={`${installer.id}-${day}`}
                          className="text-xs font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {day.slice(0, 3)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
